import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Money as MoneyComponent } from '../../../Money';
import { Meal as MealType, MealCounter, MealViewMode } from '../../types';
import { useIsDesktop } from '../../../utils';
import { useTheme } from '../../../theme';
import { Money } from '../../../types';
import ModalX, { ModalXProps } from '../../../BaseComponents/ModalX';
import Details from '../Details/Details';
import { useRenderers } from '../../../renderProps';
import { useToggleable } from '../../../hooks';
import { useCounters } from '@entities/counters';

export interface Props {
	meal: MealType;
	counters: MealCounter[];
	onConfirm: (mealCounters: MealCounter[]) => void;
	onClose?: (meal: MealType) => void;
	marker?: string;
	oldPrice?: Money;
	fullSize?: boolean;
	readonly?: boolean;
	isLoading?: boolean;
	mode?: MealViewMode;
	onSubmit?: () => void;
	onPopupOpened?: (meal: MealType) => void;
}

const Meal: React.FunctionComponent<Props> = ({
	meal,
	marker,
	oldPrice,
	counters: initialCounters = [],
	onConfirm,
	readonly,
	fullSize,
	isLoading,
	onPopupOpened,
	mode,
	onClose
}) => {
	const theme = useTheme('Meal').Meal;
	const { t } = useTranslation('Meal');

	const {
		counters,
		isChanged: isCountChanged,
		onDecrease,
		onIncrease,
		onReset,
		onClear,
		onAddForAllPassengers
	} = useCounters<MealCounter>(initialCounters);

	const totalNumberOfMeals = initialCounters.reduce((total, counter) => counter.count + total, 0);

	const isDesktop = useIsDesktop();

	const { isOpen, open, close } = useToggleable(false);

	const { MealDetails: MealDetailsOverride = Details } = useRenderers();

	const openModal = useCallback(() => {
		onPopupOpened?.(meal);
		open();
	}, [meal, onPopupOpened]);

	const onDismiss = () => {
		close();
		onReset(initialCounters);
	};

	const onConfirmHandler = () => {
		if (isCountChanged) {
			onConfirm(counters);
		}
		close();
	};

	const onClearHandler = () => {
		onClear();
	};

	return (
		<>
			<div
				className={cn(theme.meal, {
					[theme.meal_fullSize]: fullSize && isDesktop
				})}
				data-test-id="meal-card"
				onClick={openModal}
			>
				<div className={theme.header}>
					<div className={theme.image} style={meal.icon ? { backgroundImage: `url(${meal.icon})` } : {}}>
						{(!fullSize || !isDesktop) && marker && <div className={theme.marker}>{marker}</div>}
					</div>

					{(!fullSize || !isDesktop) && (
						<div
							className={cn(theme.title, {
								[theme.meal__item_selected_borderTitle]: totalNumberOfMeals > 0
							})}
						>
							<div className={cn(theme.name, { [theme.hasWeight]: true })}>{meal.name}</div>
						</div>
					)}
				</div>

				<div className={cn(theme.footer, { [theme.meal__item_selected_borderFooter]: totalNumberOfMeals > 0 })}>
					{fullSize && isDesktop && marker && <div className={theme.marker}>{marker}</div>}

					{fullSize && isDesktop && (
						<div className={theme.title}>
							<div className={theme.name}>{meal.name}</div>
						</div>
					)}

					<div className={theme.footer__icons}>
						{!!oldPrice?.amount && <MoneyComponent moneyClassName={theme.oldPrice} money={oldPrice} />}

						{!!meal.price.amount ? (
							<MoneyComponent
								moneyClassName={cn(theme.price, {
									[theme.meal__item_selected]: totalNumberOfMeals > 0
								})}
								money={meal.price}
							/>
						) : (
							<span className={theme.price}>{t('Free')}</span>
						)}

						{totalNumberOfMeals > 0 && <div className={theme.count}>x{totalNumberOfMeals}</div>}
					</div>
				</div>
			</div>

			<ModalX
				keepMounted={false}
				isOpen={isOpen && mode !== MealViewMode.confirmed}
				onClose={onDismiss}
				paperProps={{ 'data-test-id': 'meal-popup' } as ModalXProps['paperProps']}
				classes={{
					scrollPaper: theme.popup__scrollPaper,
					root: theme.popup__root,
					paper: theme.popup,
					closeButton: theme.popup__close
				}}
			>
				<MealDetailsOverride
					onAddForAll={() => onAddForAllPassengers(meal.allowedPassengers)}
					marker={marker}
					meal={meal}
					oldPrice={oldPrice}
					counters={counters}
					onDecrease={onDecrease}
					onIncrease={onIncrease}
					onClear={onClearHandler}
					readonly={readonly}
					isLoading={isLoading}
					isCountChanged={isCountChanged}
					onSubmit={onConfirmHandler}
				/>
			</ModalX>
		</>
	);
};

export default Meal;
